import type * as React from "react";
import { type CSSProperties, type ReactNode } from "react";
import { styled, type Theme } from "~/common/mui";
import {
  C_OUTLINE_VARIANT,
  C_PRIMARY_DISABLED,
  C_PRIMARY_HOVER,
  C_SECONDARY_DISABLED,
  C_SECONDARY_HOVER,
  C_WHITE,
} from "~/common/components/icon/Colors";

type DivProps = JSX.IntrinsicElements["div"];

export interface ActionIconButtonProps extends DivProps {
  icon?: ReactNode;
  onIconClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  className?: string;
  style?: CSSProperties;
  clickableType?: ActionIconButtonType;
  disabled?: boolean;
  inverted?: boolean;
  id?: string;
  children?: ReactNode;
}

export enum ActionIconButtonType {
  PRIMARY = "primary",
  SECONDARY = "secondary",
  INFO = "info",
  WHITE = "white",
  ERROR = "error",
}

type StyledRootProps = {
  $clickableType: ActionIconButtonType;
  $inverted: boolean;
  $disabled: boolean;
};

const colorStyles = (
  theme: Theme,
  clickableType: ActionIconButtonType,
  inverted: boolean,
  disabled: boolean,
): React.CSSProperties => {
  if (disabled) {
    switch (clickableType) {
      case ActionIconButtonType.PRIMARY:
        return { color: C_PRIMARY_DISABLED };
      case ActionIconButtonType.SECONDARY:
      case ActionIconButtonType.INFO:
      case ActionIconButtonType.ERROR:
        return { color: C_SECONDARY_DISABLED };
      default:
        return {};
    }
  }

  if (inverted) {
    switch (clickableType) {
      case ActionIconButtonType.PRIMARY:
        return {
          background: theme.palette.primary.main,
          color: C_WHITE,
          "&:hover": { background: C_PRIMARY_HOVER },
        } as React.CSSProperties;
      case ActionIconButtonType.SECONDARY:
        return {
          color: C_WHITE,
          background: theme.palette.text.primary,
          "&:hover": { background: C_SECONDARY_HOVER },
        } as React.CSSProperties;
      case ActionIconButtonType.INFO:
        return { color: C_WHITE };
      default:
        return {};
    }
  }

  switch (clickableType) {
    case ActionIconButtonType.PRIMARY:
      return {
        color: theme.palette.primary.main,
        "&:hover": { color: C_PRIMARY_HOVER },
      } as React.CSSProperties;
    case ActionIconButtonType.SECONDARY:
      return {
        color: theme.palette.text.primary,
        "&:hover": { color: C_SECONDARY_HOVER },
      } as React.CSSProperties;
    case ActionIconButtonType.WHITE:
      return {
        color: C_WHITE,
        "&:hover": { color: C_OUTLINE_VARIANT },
      } as React.CSSProperties;
    case ActionIconButtonType.ERROR:
      return { color: theme.palette.error.main };
    default:
      return {};
  }
};

const StyledRoot = styled("div", {
  shouldForwardProp: (prop) => !["$clickableType", "$inverted", "$disabled"].includes(prop as string),
})<StyledRootProps>(({ theme, $clickableType, $inverted, $disabled }) => ({
  cursor: $disabled ? "default" : "pointer",
  display: $inverted ? "flex" : "inline-flex",
  ...($inverted && {
    width: 36,
    height: 36,
    justifyContent: "center",
    alignItems: "center",
  }),
  ...($disabled && {
    pointerEvents: "none",
  }),
  ...colorStyles(theme, $clickableType, $inverted, $disabled),
}));

export const ActionIconButton = (props: ActionIconButtonProps) => {
  const {
    icon,
    onIconClick,
    clickableType = ActionIconButtonType.PRIMARY,
    className,
    inverted = false,
    disabled = false,
    children,
    ...rest
  } = props;

  return (
    <StyledRoot
      $clickableType={clickableType}
      $inverted={inverted}
      $disabled={disabled}
      className={className}
      onClick={onIconClick}
      {...rest}
    >
      {icon}
      {children}
    </StyledRoot>
  );
};
